import { useCallback, useEffect, useMemo } from 'react'
import { selectFeatureIncludes, FeatureDict } from '@authed/selectors'
import { addDefaultHidden } from '@datasets/handsontable/read-grid/readGridToolbar/components/toggleColumns/utils'
import { transformDatasetToById } from '@global/api/transforms'
import { useCreativeClassifications } from '@global/hooks/queries/useCreativeClassifications'
import { useDatasetsDataCache } from '@global/hooks/queries/useDatasetsDataCache'
import { useDatasetDataQuery } from '@global/hooks/queries/useDatasetsDataQuery'
import { useTemplate } from '@global/hooks/useTemplate'
import * as SnackbarActions from '@global/snackbar/snackbar.actions'
import { makeQueryErrorMessage } from '@global/utils/reactQuery'
import { useAppDispatch, useAppSelector } from '@redux/hooks'
import { useSettings } from '../../dataset-details/hooks/useSettings'
import { useRefetchFreshDataset } from '../../hooks/useRefetchFreshDataset'
import { makeQueryOptionsDto } from '../utils'
import { AxiosError, CanceledError } from 'axios'
import { MakeQueryOptionsDtoArgs } from '@global/api/types/queryOptions'
import { AnyAction } from '@reduxjs/toolkit'

interface IUseDatasetsQueryProps {
  datasetId: string
  reducerDispatch: React.Dispatch<AnyAction>
  queryOptionsByFieldId: MakeQueryOptionsDtoArgs['optionsByFieldId']
  limit: number
  offset: number
  commitRef: string
  showHiddenColumns: boolean
  templateId: string
}

interface IUseDatasetsQueryReturn {
  refetchDataset: () => void
}

export const useDatasetsQuery = ({
  datasetId,
  reducerDispatch,
  queryOptionsByFieldId,
  limit,
  offset,
  commitRef,
  showHiddenColumns,
  templateId,
}: IUseDatasetsQueryProps): IUseDatasetsQueryReturn => {
  const dispatch = useAppDispatch()
  const [APPROVAL_ENABLED = false] = useAppSelector(selectFeatureIncludes([FeatureDict.APPROVAL]))
  const { requireApproval } = useSettings()
  const hasCommentSupport = APPROVAL_ENABLED && requireApproval

  const { refetchDataset, refetchDatasetNum, refetchDatasetRefNum } = useRefetchFreshDataset()
  const { template } = useTemplate({ templateId })
  const { data: classifications = [] } = useCreativeClassifications()

  const clearCommitRefToRefresh = refetchDatasetRefNum !== refetchDatasetNum

  const queryOptions = makeQueryOptionsDto({
    optionsByFieldId: queryOptionsByFieldId,
    queryOffset: offset,
    queryLimit: limit,
    queryCommitRef: clearCommitRefToRefresh ? commitRef : '',
    includeComments: hasCommentSupport,
    includeRowProperties: true,
    classificationCategoryIds: classifications.map(({ id }) => id),
  })

  // TODO: there is global react-query error handling in the app/index.jsx file,
  // it would be great to consolidate, or at least have a global error handling unit test suite
  const handleQueryError = useCallback(
    (error: unknown) => {
      // Generate the error message using a utility function
      const message = makeQueryErrorMessage(error as Error)

      if (error instanceof AxiosError && error.status === 404) {
        return
      }

      // Check if the request was cancelled
      if (error instanceof CanceledError) {
        reducerDispatch({ type: 'setDatasetLoading', payload: false })
      } else if (message) {
        dispatch(SnackbarActions.showSnackbar({ type: 'ERROR', message }))
      }
    },
    [dispatch, reducerDispatch],
  )

  const { addDatasetDataById } = useDatasetsDataCache()
  const { data, isInitialLoading: isLoading } = useDatasetDataQuery({
    params: {
      datasetId,
      queryOptionsByFieldId,
      offset,
      limit,
      hasCommentSupport,
      refetchDatasetNum,
      refetchDatasetRefNum,
      classificationCategoryIds: queryOptions.classificationCategoryIds,
    },
    queryParams: {
      datasetId,
      queryOptions,
      errorCallback: handleQueryError,
    },
  })

  const {
    rows,
    headers: columns,
    commitRef: datasetCommitRef,
    queryRowCount,
    datasetRowCount,
  } = data ?? {}

  const transformation = useMemo(
    () =>
      rows && columns
        ? transformDatasetToById({
            id: datasetId,
            rows,
            columns: columns.map(addDefaultHidden),
            showHidden: showHiddenColumns,
            addRowPropertyFields: true,
            creativeConfiguration: template?.creativeConfiguration,
            classifications,
          })
        : {},
    [rows, columns, showHiddenColumns, template?.creativeConfiguration],
  )

  // On completion of useQuery
  useEffect(() => {
    if (isLoading || !data) return

    addDatasetDataById(transformation)

    // FIXME: How do we eliminate this reducer update? These should be set by the triggers that
    // change these
    reducerDispatch({
      type: 'setDatasetData',
      payload: {
        commitRef: datasetCommitRef ?? '',
        queryRowCountIsLoading: false,
        queryRowCount: queryRowCount ?? 0,
        datasetRowCount: datasetRowCount ?? 0,
        updatingFilters: false,
        datasetLoading: false,
      },
    })
  }, [transformation, isLoading, data, template?.creativeConfiguration])

  return {
    refetchDataset,
  }
}
